import { ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  ListItemIcon,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import {
  MORE_KEY,
  MORE_LABEL,
  NAV_ITEMS,
  PRIMARY_NAV_ITEMS,
  getActiveNavItem,
} from '../../constants/navigation';

const MORE_MENU_ID = 'nav-more-menu';
const MORE_BUTTON_ID = 'nav-more-button';

/**
 * 데스크톱 상단 메뉴.
 * - lg(1200px) 이상: 주 메뉴 7개(홈 포함) + "더보기"(시뮬레이션/전략투자/설정)
 * - md~lg: 주 메뉴를 4개(홈·포트폴리오·계좌·거래내역)로 줄이고 나머지는 "더보기" 로 넘김
 * - 항목은 실제 <a href> (router Link) 라서 가운데 클릭/새 탭이 동작한다.
 * - 현재 경로가 목록에 없으면 어떤 항목도 선택되지 않는다.
 */
const Navigation = () => {
  const location = useLocation();
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const showIcons = useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true });
  const [moreAnchor, setMoreAnchor] = useState(null);

  const visibleItems = isWide
    ? PRIMARY_NAV_ITEMS
    : PRIMARY_NAV_ITEMS.filter((item) => !item.collapseBelowLg);
  const visibleKeys = new Set(visibleItems.map((item) => item.key));
  const overflowItems = NAV_ITEMS.filter((item) => !visibleKeys.has(item.key));

  const activeItem = getActiveNavItem(location.pathname);
  const activeKey = activeItem ? activeItem.key : null;
  const isMoreActive = overflowItems.some((item) => item.key === activeKey);
  const isMoreOpen = Boolean(moreAnchor);

  const closeMore = () => setMoreAnchor(null);

  // 라이트: primary 바 위 흰 글자 + secondary 밑줄 (기존 모양)
  // 다크: AppBar 가 어두운 표면(paper)이므로 활성 항목을 primary 색 글자 + primary 밑줄로 표시
  const isDark = theme.palette.mode === 'dark';
  const buttonSx = (active) => ({
    color: isDark && active ? theme.palette.primary.main : 'inherit',
    textTransform: 'none',
    fontSize: '0.95rem',
    fontWeight: active ? 700 : 500,
    minHeight: 48,
    px: 1.5,
    borderRadius: 0,
    whiteSpace: 'nowrap',
    borderBottom: '2px solid',
    borderBottomColor: active
      ? (isDark ? theme.palette.primary.main : theme.palette.secondary.main)
      : 'transparent',
    '&:hover': {
      backgroundColor: isDark
        ? theme.palette.action.hover
        : alpha(theme.palette.primary.contrastText, 0.08),
    },
  });

  return (
    <Box
      component="nav"
      aria-label="주 메뉴"
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}
    >
      {visibleItems.map((item) => {
        const active = item.key === activeKey;
        return (
          <Button
            key={item.key}
            component={RouterLink}
            to={item.path}
            aria-current={active ? 'page' : undefined}
            startIcon={showIcons ? <item.Icon /> : undefined}
            sx={buttonSx(active)}
          >
            {item.label}
          </Button>
        );
      })}

      {overflowItems.length > 0 && (
        <>
          <Button
            id={MORE_BUTTON_ID}
            aria-haspopup="menu"
            aria-controls={isMoreOpen ? MORE_MENU_ID : undefined}
            aria-expanded={isMoreOpen ? 'true' : undefined}
            aria-current={isMoreActive ? 'true' : undefined}
            data-nav-key={MORE_KEY}
            endIcon={<ExpandMore />}
            onClick={(event) => setMoreAnchor(event.currentTarget)}
            sx={buttonSx(isMoreActive)}
          >
            {MORE_LABEL}
          </Button>
          <Menu
            id={MORE_MENU_ID}
            anchorEl={moreAnchor}
            open={isMoreOpen}
            onClose={closeMore}
            MenuListProps={{ 'aria-labelledby': MORE_BUTTON_ID }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {overflowItems.map((item) => {
              const active = item.key === activeKey;
              return (
                <MenuItem
                  key={item.key}
                  component={RouterLink}
                  to={item.path}
                  selected={active}
                  aria-current={active ? 'page' : undefined}
                  onClick={closeMore}
                >
                  <ListItemIcon>
                    <item.Icon fontSize="small" />
                  </ListItemIcon>
                  {item.label}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default React.memo(Navigation);
